<template>
  <div class="container p-0 mt-3" :class="{ spinner: isBusy }">
    <!-- Result Threshold -->
    <div class="form-group form-row">
      <label class="col-lg-4 col-md-6 col-form-label pt-0">
        {{ $t('Webportal.Administration.CheckRevisionQuestionSettings.ResultThreshold') }}
        <span
          v-b-tooltip.hover.nofade="toolTipDelay"
          :title="$t('Webportal.Administration.CheckRevisionQuestionSettings.ResultThreshold.Description')"
        >
          <font-awesome-icon :icon="['fas', 'info-circle']" />
        </span>
      </label>
      <div class="col">
        <dx-number-box
          v-model="resultThreshold"
          class="custom-control-inline"
          width="8em"
          height="2em"
          format="##0'%'"
          :show-spin-buttons="true"
          :step="1"
          :min="0"
          :max="100"
          :show-clear-button="true"
          :disabled="isBusy || disabled"
        />
      </div>
    </div>

    <!-- Question Selection Treeselect -->
    <div class="form-group form-row">
      <label class="col-lg-4 col-md-6 col-form-label pt-0">
        {{ $t('Webportal.Administration.CheckRevisionQuestionSettings.Question') }}
        <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.CheckRevisionQuestionSettings.Question.Description')">
          <font-awesome-icon :icon="['fas', 'info-circle']" />
        </span>
      </label>
      <div class="col">
        <treeselect
          v-model="selectedQuestionId"
          :options="questionItems"
          :disabled="disabled || isBusy"
          :disableFuzzyMatching="true"
          :showCount="true"
          showCountOf="LEAF_DESCENDANTS"
          :defaultExpandLevel="1"
          :disable-branch-nodes="true"
          :placeholder="$t('Webportal.Administration.CheckRevisionQuestionSettings.Question.Placeholder')"
        >
          <template #option-label="{ node, shouldShowCount, count, countClassName }">
            <div v-if="node.isBranch">
              {{ node.label }}
              <span v-if="shouldShowCount" :class="countClassName">({{ count }})</span>
            </div>
            <div v-else>
              <font-awesome-icon
                v-if="node.raw.isComplainable"
                :icon="['far', 'check-circle']"
                class="mr-1 text-success"
                v-b-tooltip.hover.nofade="toolTipDelay"
                :title="$t('Webportal.Administration.CheckRevisionQuestionSettings.QuestionComplainable')"
              />
              <font-awesome-icon
                v-else
                :icon="['far', 'circle']"
                class="mr-1"
                v-b-tooltip.hover.nofade="toolTipDelay"
                :title="$t('Webportal.Administration.CheckRevisionQuestionSettings.QuestionNotComplainable')"
              />
              {{ node.label }}
              <span v-if="node.raw.maxPoints" class="font-weight-bold">{{ $t('Webportal.Administration.CheckRevisionQuestionSettings.MaxPoints', { points: node.raw.maxPoints }) }}</span>
              <b-badge v-if="node.raw.hasGuideline" variant="info" class="ml-1">
                {{ $t('Webportal.Administration.CheckRevisionQuestionSettings.Guideline') }}
                <span
                  v-for="localizationId in node.raw.guidelineLocalizations"
                  :key="localizationId"
                  class="ml-1"
                  v-b-tooltip.hover.nofade="toolTipDelay"
                  :title="getLanguageName(localizationId)"
                >
                  <font-awesome-icon v-if="localizationId === 'deflt'" icon="wrench" fixed-width />
                  <span v-else :class="['flag-icon', 'flag-icon-' + getRegion(localizationId)]"></span>
                </span>
              </b-badge>
              <b-badge v-if="node.raw.isAttachmentMandatory" variant="warning" class="ml-1">{{ $t('Webportal.Administration.CheckRevisionQuestionSettings.Attachment') }}</b-badge>
            </div>
          </template>
        </treeselect>
      </div>
    </div>

    <!-- Question Detail Area -->
    <div class="mt-3">
      <div class="card">
        <div class="card-header font-weight-bold">
          <span v-if="selectedQuestion">{{ selectedQuestion.QuestionTextLong }}</span>
          <span v-else>{{ $t('Webportal.Administration.CheckRevisionQuestionSettings.NoQuestionSelected') }}</span>
        </div>
        <div class="card-body" style="min-height: 300px">
          <template v-if="selectedQuestion">
            <!-- Leitfaden Section -->
            <h5>
              <font-awesome-icon :icon="['fas', 'book']" class="mr-1" />
              {{ $t('Webportal.Administration.CheckRevisionQuestionSettings.Guideline') }}
              <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.CheckRevisionQuestionSettings.Guideline.Description')">
                <font-awesome-icon :icon="['fas', 'info-circle']" />
              </span>
            </h5>
            <guideline-editor
              :guidelineTexts="selectedQuestion.GuidelineTexts"
              :allowedLocalizations="availableLocalizations"
              :localizationData="localizationData"
              :disabled="isBusy || disabled"
              @change="hasChanges = true"
            />

            <hr />

            <!-- Reklamierbarkeit Section -->
            <h5>
              <font-awesome-icon :icon="['fas', 'gavel']" class="mr-1" />
              {{ $t('Webportal.Administration.CheckRevisionQuestionSettings.Complainability') }}
            </h5>
            <question-complainability
              :question="selectedQuestion"
              :questionComplainability="selectedQuestionComplainability"
              :answerComplainability="selectedAnswerComplainability"
              :onlyQuestionsWithScoring="onlyQuestionsWithScoring"
              :disabled="isBusy || disabled"
              @update:question-complainability="onQuestionComplainabilityChange"
              @update:answer-complainability="onAnswerComplainabilityChange"
            />

            <hr />

            <!-- Nachweis Section -->
            <h5>
              <font-awesome-icon :icon="['fas', 'paperclip']" class="mr-1" />
              {{ $t('Webportal.Administration.CheckRevisionQuestionSettings.Attachments') }}
            </h5>
            <div class="form-group form-row">
              <label class="col-lg-4 col-md-6 col-form-label pt-0">
                {{ $t('Webportal.Administration.CheckRevisionQuestionSettings.AttachmentMandatory') }}
                <span v-b-tooltip.hover.nofade="toolTipDelay" :title="$t('Webportal.Administration.CheckRevisionQuestionSettings.AttachmentMandatory.Description')">
                  <font-awesome-icon :icon="['fas', 'info-circle']" />
                </span>
              </label>
              <div class="col-auto">
                <label class="switch switch-label switch-secondary">
                  <input
                    class="switch-input"
                    type="checkbox"
                    :checked="getAttachmentMandatory()"
                    @input="updateAttachmentMandatory($event.target.checked)"
                    :disabled="isBusy || disabled || !isSelectedQuestionComplainable"
                  />
                  <span class="switch-slider" data-checked="On" data-unchecked="Off"></span>
                </label>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
    
    <!-- Apply Button -->
    <div class="d-flex justify-content-end mt-3">
      <b-button variant="primary" @click="applyData" :disabled="isBusy || !hasChanges">
        <font-awesome-icon :icon="['fas', 'share-all']" /> {{ $t('Webportal.Apply') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import AppConfig from "@/mixins/AppConfig-APP_TARGET";
import ActionService from "@/services/ActionService";
import SurveysService from "@/services/SurveysService";
import LocalizationsService from "@/services/LocalizationsService";
import EventBus from "@/services/EventBus.js";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import { DxNumberBox } from "devextreme-vue";
import GuidelineEditor from "./GuidelineEditor";
import QuestionComplainability from "./QuestionComplainability";

export default {
  name: "CheckRevisionQuestionSettings",
  mixins: [AppConfig],
  components: { Treeselect, DxNumberBox, GuidelineEditor, QuestionComplainability },

  props: {
    surveyId: { type: Number, default: null },
    surveySettings: { type: Object, default: null },
    disabled: { type: Boolean, default: false },
  },

  data() {
    return {
      isBusy: false,
      questionData: [],
      configurationData: {},
      guidelineData: [],
      guidelineLocalizations: [],
      localizationData: [],
      selectedQuestionId: null,
      hasChanges: false,
      resultThreshold: null,
      questionComplainabilityMap: {},
      answerComplainabilityMap: {},
      attachmentMandatoryMap: {},
      dataLoaded: false,
    };
  },

  computed: {
    selectedQuestion() {
      if (!this.selectedQuestionId || !this.questionData) return null;
      return this.questionData.find((q) => q.ExternalSurveyQuestionId === this.selectedQuestionId);
    },

    /**
     * Resolves the effective SurveyRevisionOnlyQuestionsWithScoring value
     * using the same fallback logic as SurveySettings.getSettingValue:
     * survey-level value ?? client-level value.
     */
    onlyQuestionsWithScoring() {
      if (!this.surveySettings) return true;
      return (
        this.surveySettings.SurveyRevisionOnlyQuestionsWithScoring ??
        this.surveySettings.Client?.SurveyRevisionOnlyQuestionsWithScoring ??
        true
      );
    },

    /**
     * LocalizationIds that can be maintained for the current survey.
     * Delivered by ACTION.ReadSurveyGuidelineTexts ('deflt' + survey default
     * localization + client location default localizations + localizations
     * already used in existing guideline texts).
     */
    availableLocalizations() {
      const ids = this.guidelineLocalizations.filter((id) => !!id);
      return ids.includes("deflt") ? ids : ["deflt", ...ids];
    },

    isSelectedQuestionComplainable() {
      return this.selectedQuestionComplainability === true;
    },

    selectedQuestionComplainability() {
      if (!this.selectedQuestionId) return false;
      return this.questionComplainabilityMap[this.selectedQuestionId] ?? false;
    },

    selectedAnswerComplainability() {
      if (!this.selectedQuestionId) return {};
      return this.answerComplainabilityMap[this.selectedQuestionId] ?? {};
    },

    questionItems() {
      if (!this.questionData || this.questionData.length === 0) return [];

      return _(this.questionData)
        // filter out questions without cluster and all helper questions (start with q and 5 following digits)
        .filter((q) => q.ClusterText && q.QuestionTextLong && !/^q\d{5}/.test(q.QuestionTextLong))
        .groupBy((q) => q.ClusterText)
        .map((questions, clusterText) => ({
          id: `cluster_${clusterText}`,
          label: clusterText,
          children: questions.map((q) => {
            const isComplainable = this.questionComplainabilityMap[q.ExternalSurveyQuestionId] ?? this.getDefaultComplainability(q);
            const filledGuidelines = (q.GuidelineTexts ?? []).filter((g) => g.Title || g.Description);
            const hasGuideline = filledGuidelines.length > 0;
            // Default language first, remaining languages alphabetically
            const guidelineLocalizations = _.orderBy(
              filledGuidelines.map((g) => g.LocalizationId),
              [(locId) => (locId === "deflt" ? 0 : 1), (locId) => locId],
              ["asc", "asc"]
            );
            const isAttachmentMandatory = this.attachmentMandatoryMap[q.ExternalSurveyQuestionId] ?? false;

            return {
              id: q.ExternalSurveyQuestionId,
              label: q.QuestionTextLong,
              maxPoints: q.MaxPoints,
              hasGuideline,
              guidelineLocalizations,
              isComplainable,
              isAttachmentMandatory,
            };
          }),
        }))
        .value();
    },
  },

  watch: {
    surveyId() {
      this.selectedQuestionId = null;
      this.hasChanges = false;
      if (this.dataLoaded) {
        this.loadData();
      } else {
        this.dataLoaded = false;
      }
    },
    resultThreshold() {
      if (this.dataLoaded) this.hasChanges = true;
    },
  },

  mounted() {
    this.loadLocalizations();
    EventBus.$on("LocalizationChanged", this.onLocalizationChanged);
  },

  beforeDestroy() {
    EventBus.$off("LocalizationChanged", this.onLocalizationChanged);
  },

  methods: {
    onLocalizationChanged() {
      this.loadData();
    },

    getRegion(localizationId) {
      if (!localizationId || !localizationId.includes("-")) return "";
      return localizationId.split("-")[1].toLowerCase();
    },

    loadLocalizations() {
      LocalizationsService.getLocalizationTexts().then((response) => {
        this.localizationData = response?.data ?? [];
      });
    },

    /**
     * Resolves the display name of a localization, using the same logic as GuidelineEditor.
     */
    getLanguageName(localizationId) {
      const group = this.localizationData.filter((e) => e.localizationTextId === localizationId);

      if (group.length > 0) {
        return group.length > 1 ? `${group[0].languageNameShort} - ${group[1].languageNameShort}` : group[0].languageNameShort;
      }

      // Fallback for when localization data is not loaded yet
      return localizationId === "deflt" ? this.$t('Webportal.Default') : localizationId;
    },

    loadData() {
      if (!this.surveyId) {
        this.questionData = [];
        this.configurationData = {};
        this.guidelineData = [];
        this.guidelineLocalizations = [];
        return;
      }

      this.isBusy = true;
      Promise.all([
        SurveysService.getSurveyQuestions([this.surveyId], true, null, true),
        ActionService.getSurveyQuestionConfiguration(this.surveyId),
        ActionService.getSurveyGuidelineTexts(this.surveyId),
      ])
        .then(([responseQuestions, responseConfig, responseGuidelines]) => {
          // Questions (extract from array wrapper)
          if (responseQuestions?.data?.length >= 1 && responseQuestions.data[0].Questions)
            this.questionData = responseQuestions.data[0].Questions;
          else this.questionData = [];

          // Configuration data
          this.configurationData = responseConfig?.data && !Array.isArray(responseConfig.data) ? responseConfig.data : {};
          this.resultThreshold = this.configurationData.ResultThreshold ?? null;

          // Guideline texts (nested per question) and available localizations
          const guidelinesResult = responseGuidelines?.data ?? {};
          const guidelinesRaw = Array.isArray(guidelinesResult.Questions) ? guidelinesResult.Questions : [];
          this.guidelineData = guidelinesRaw;
          this.guidelineLocalizations = (
            Array.isArray(guidelinesResult.Localizations) ? guidelinesResult.Localizations : []
          ).map((l) => l.LocalizationId);

          // Merge guideline texts into question objects
          for (const q of this.questionData) {
            const match = guidelinesRaw.find((g) => g.ExternalSurveyQuestionId === q.ExternalSurveyQuestionId);
            this.$set(q, "GuidelineTexts", match?.GuidelineTexts ?? []);
          }

          this.initializeComplainabilityMaps();
          this.dataLoaded = true;
          this.hasChanges = false;
        })
        .catch((error) => {
          console.error(error);
          this.$bvToast.toast(this.$t('Webportal.Administration.CheckRevisionQuestionSettings.LoadError'), {
            variant: "danger",
            autoHideDelay: 5000,
            noCloseButton: true,
          });
        })
        .finally(() => {
          this.isBusy = false;
        });
    },

    /**
     * Returns the default complainability for a question.
     * When onlyQuestionsWithScoring is true:
     *   - Choice questions with at least one non-comment answer having Points != null: true
     *   - All other questions: false
     * When onlyQuestionsWithScoring is false:
     *   - All questions are complainable by default: true
     */
    getDefaultComplainability(question) {
      if (!this.onlyQuestionsWithScoring) return true;
      if (!question.IsCommentPossible && question.Answers) {
        return question.Answers.some((answer) => answer.Points != null);
      }
      return false;
    },

    initializeComplainabilityMaps() {
      this.questionComplainabilityMap = {};
      this.answerComplainabilityMap = {};
      this.attachmentMandatoryMap = {};

      for (const question of this.questionData) {
        const questionConfig = (this.configurationData?.QuestionConfigurations ?? []).find(
          (qc) => qc.ExternalSurveyQuestionId === question.ExternalSurveyQuestionId
        );

        // Question-level complainability: use persisted value or derive default
        const defaultComplainability = this.getDefaultComplainability(question);
        this.$set(
          this.questionComplainabilityMap,
          question.ExternalSurveyQuestionId,
          questionConfig != null ? questionConfig.IsComplainable : defaultComplainability
        );

        // Attachment mandatory
        this.$set(this.attachmentMandatoryMap, question.ExternalSurveyQuestionId, questionConfig?.IsAttachmentMandatory ?? false);

        // Answer-level complainability (choice questions): default is true (Reklamierbar)
        if (!question.IsCommentPossible && question.Answers) {
          const answerConfigs = (this.configurationData?.AnswerConfigurations ?? []).filter(
            (ac) => ac.ExternalSurveyQuestionId === question.ExternalSurveyQuestionId
          );

          const map = {};
          for (const answer of question.Answers) {
            const ac = answerConfigs.find((ac2) => ac2.ExternalSurveyAnswerId === answer.ExternalSurveyAnswerId);
            map[answer.ExternalSurveyAnswerId] = ac ? false : true;
          }
          this.$set(this.answerComplainabilityMap, question.ExternalSurveyQuestionId, map);
        }
      }
    },

    // --- Event handlers from sub-components ---

    onQuestionComplainabilityChange(value) {
      this.$set(this.questionComplainabilityMap, this.selectedQuestionId, value);
      this.hasChanges = true;
    },

    onAnswerComplainabilityChange(answerId, value) {
      if (!this.answerComplainabilityMap[this.selectedQuestionId]) {
        this.$set(this.answerComplainabilityMap, this.selectedQuestionId, {});
      }
      this.$set(this.answerComplainabilityMap[this.selectedQuestionId], answerId, value);
      this.hasChanges = true;
    },

    // --- Attachment mandatory ---

    getAttachmentMandatory() {
      if (!this.selectedQuestionId) return false;
      return this.attachmentMandatoryMap[this.selectedQuestionId] ?? false;
    },

    updateAttachmentMandatory(value) {
      this.$set(this.attachmentMandatoryMap, this.selectedQuestionId, value);
      this.hasChanges = true;
    },

    // --- Save logic ---

    buildConfigPayload() {
      const questionConfigurations = [];

      for (const q of this.questionData) {
        const defaultComplainability = this.getDefaultComplainability(q);
        const complainability = this.questionComplainabilityMap[q.ExternalSurveyQuestionId] ?? defaultComplainability;
        const attachmentMandatory = this.attachmentMandatoryMap[q.ExternalSurveyQuestionId] ?? false;

        // Only persist when value differs from default or has attachment config
        if (complainability !== defaultComplainability || attachmentMandatory) {
          questionConfigurations.push({
            ExternalSurveyQuestionId: q.ExternalSurveyQuestionId,
            IsComplainable: complainability,
            IsAttachmentMandatory: attachmentMandatory,
          });
        }
      }

      const answerConfigurations = this.buildAnswerConfigurations();

      return {
        ResultThreshold: this.resultThreshold,
        QuestionConfigurations: questionConfigurations,
        AnswerConfigurations: answerConfigurations,
      };
    },

    /**
     * Builds answer configuration entries (deny-only):
     * - Only persist answers set to "Nicht reklamierbar" (false)
     * - When all answers are "Reklamierbar" (true), no entries are persisted
     */
    buildAnswerConfigurations() {
      const result = [];

      for (const question of this.questionData) {
        if (question.IsCommentPossible || !question.Answers) continue;

        const answerStates = this.answerComplainabilityMap[question.ExternalSurveyQuestionId];
        if (!answerStates) continue;

        for (const answer of question.Answers) {
          if (answerStates[answer.ExternalSurveyAnswerId] === false) {
            result.push({
              ExternalSurveyQuestionId: question.ExternalSurveyQuestionId,
              ExternalSurveyAnswerId: answer.ExternalSurveyAnswerId,
              IsComplainable: false,
            });
          }
        }
      }

      return result;
    },

    buildGuidelinePayload() {
      const result = [];
      for (const q of this.questionData) {
        const texts = (q.GuidelineTexts ?? []).filter((g) => g.Title || g.Description);
        if (texts.length > 0) {
          result.push({
            ExternalSurveyQuestionId: q.ExternalSurveyQuestionId,
            GuidelineTexts: texts.map((g) => ({
              LocalizationId: g.LocalizationId,
              Title: g.Title || "",
              Description: g.Description || "",
            })),
          });
        }
      }
      return result;
    },

    async applyData() {
      this.isBusy = true;
      try {
        const configPayload = this.buildConfigPayload();
        const guidelinePayload = this.buildGuidelinePayload();

        await Promise.all([
          ActionService.setSurveyQuestionConfiguration(this.surveyId, configPayload),
          ActionService.setSurveyGuidelineTexts(this.surveyId, guidelinePayload),
        ]);

        this.$bvToast.toast(this.$t('Webportal.Administration.CheckRevisionQuestionSettings.SaveSuccess'), {
          autoHideDelay: 2000,
          variant: "success",
          noCloseButton: true,
        });
        this.hasChanges = false;
      } catch (error) {
        console.error(error);
        this.$bvToast.toast(this.$t('Webportal.Administration.CheckRevisionQuestionSettings.SaveError'), {
          autoHideDelay: 10000,
          variant: "danger",
          noCloseButton: true,
        });
      } finally {
        this.isBusy = false;
      }
    },
  },
};
</script>
